import { useEffect, useState, useRef } from "react"
import { View, Pressable, FlatList, Platform } from "react-native"
import {
  $selectedCompDetailId,
  setSelectedCompDetailId,
} from "@/stores/compsDataStore"
import { type PropertyType } from "@/generated/graphql"
import { type AnalysisWithCompsType } from "@/queries/analysis"

import useCompsData from "@/hooks/useCompsData"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { useStore } from "@nanostores/react"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { cn } from "@/components/util/cn"
import { CompAvatarImage } from "@/components/analysis/CompAvatarImage"
import Animated from "react-native-reanimated"

const COMP_WIDTH = Platform.OS === "web" ? 86 : 84

interface CompDetailsHorizontalThumbViewProps {
  analysis: AnalysisWithCompsType | null
  carouselDragging: boolean
}

const CompDetailsHorizontalThumbView: React.FC<
  CompDetailsHorizontalThumbViewProps
> = ({
  analysis,
  carouselDragging = false,
}: CompDetailsHorizontalThumbViewProps) => {
  const { isWeb } = useDeviceAttributes()
  const scroll = useRef<FlatList>(null)
  const [selectedIdx, setSelectedIdx] = useState<number>(0)
  const property: PropertyType | undefined = analysis?.property
  const { animatedDimStyle } = useUpdateDimming()
  const { sortedComps } = useCompsData(property)
  const selectedCompDetailId = useStore($selectedCompDetailId)

  useEffect(() => {
    if (scroll.current) {
      const idx =
        sortedComps.findIndex(
          (comp) => comp.arvcomp?.id === selectedCompDetailId
        ) || 0
      if (idx >= 0) {
        setSelectedIdx(idx)
        scroll.current?.scrollToIndex({
          animated: true,
          index: idx,
        })
      }
    }
  }, [selectedCompDetailId, analysis?.comps])

  const handlePressComp = (compId: string) => {
    setSelectedCompDetailId(compId)
  }

  return (
    <View className={cn("flex flex-col w-full")}>
      <Animated.View style={animatedDimStyle}>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={isWeb}
          // showsVerticalScrollIndicator={false}
          data={sortedComps}
          onContentSizeChange={(w, h) => {
            console.log("onContentSizeChange", w, h)
          }}
          getItemLayout={(data, index) => ({
            index,
            length: COMP_WIDTH,
            offset: index * COMP_WIDTH,
          })}
          className={"m-4"}
          renderItem={({ item, index }) => (
            <Pressable
              testID={`comp-detail-${index}`}
              className="mr-4 flex-col justify-center items-center gap-2 mb-1"
              key={item.arvcomp?.id || index}
              onPress={() => {
                handlePressComp(item.arvcomp?.id as string)
                if (!analysis) return

                AnalyzerEvents.compDetailCardClicked({
                  cardIndex: index,
                  analysis,
                  comp: {
                    id: item.arvcomp?.id,
                    address: item.arvcomp?.fullAddress || "",
                  },
                })
              }}
            >
              <CompAvatarImage analysis={analysis} comp={item} />
              <View
                className={`w-[52px] h-[3px] ${selectedIdx === index ? "bg-calm-blue rounded" : "bg-transparent"}`}
              />
            </Pressable>
          )}
          ref={scroll}
          decelerationRate="fast"
          snapToOffsets={sortedComps.reduce(
            (offsets) => {
              offsets.push(offsets[offsets.length - 1] + COMP_WIDTH)
              return offsets
            },
            [0]
          )}
        />
      </Animated.View>
    </View>
  )
}

export default CompDetailsHorizontalThumbView
